import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../../shared/models/user.model';
import { Module, ModuleAssignment } from '../../shared/models/module.model';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SystemsService {
  private apiUrl = `${environment.apiUrl}`;
  private apiUrlCore = environment.apiUrlCore;
  
  constructor(private http: HttpClient) {}

  getApiUrl(): string {
    return this.apiUrl;
  }

  // --- Usuarios ---
  getUsers(): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/objweb/usersweb`);
  }

  createUser(user: Omit<User, 'id'>): Observable<User> {
    return this.http.post<User>(`${this.apiUrl}/objweb/usersweb/add`, user);
  }

  updateUser(user: User): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/objweb/usersweb/update`, user);
  }

  updateUserPassword(payload: { id: number; password: string }): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/objweb/usersweb/pass/update`, payload);
  }

  deleteUser(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/objweb/usersweb/delete/${id}`);
  }

  getDepartments(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/departments`);
  }

  getBranches(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/branches`);
  }

  // --- Modulos ---
  getModules(): Observable<Module[]> {
    return this.http.get<Module[]>(`${this.apiUrl}/objweb/modules`);
  }

  createModule(module: Omit<Module, 'id'>): Observable<Module> {
    return this.http.post<Module>(`${this.apiUrl}/objweb/modules/add`, module);
  }

  getModulesByUser(userId: number): Observable<ModuleAssignment[]> {
    return this.http.get<ModuleAssignment[]>(`${this.apiUrl}/objweb/modules/user/${userId}`);
  }

  assignModuleToUser(payload: { userid: number; moduleid: number }): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/objweb/modules/user/add`, payload);
  }

  unassignModuleFromUser(assignmentId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/objweb/modules/user/delete/${assignmentId}`);
  }

  // --- Kit Birthday, Welcome, Consumption ---
  //---Productos de venta ---

  getSellItems(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/items/sell`);
  }

  getWarehouses(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/catalogs/warehouses`);
  }

  createInventoryExit(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrlCore}/inventory/genexit`, payload);
  }

  getItemStock(itemCode: string, whsCode: string): Observable<number> {
    const encodedItemCode = encodeURIComponent(itemCode);
    return this.http.get<any>(`${this.apiUrlCore}/items/item/stock/${encodedItemCode}/${whsCode}`).pipe(
      map(response => response.onHand) 
    );
  }

  // --- Internal consumption ---

  getEmployees(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/bp/employees`);
  }

  //--- Tastings ---
  //--- Clientes ---
  getBusinessPartnersByType(cardType: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/bp/type/${cardType}`);
  }

  //--- Clientes inactivos ---
  getInactiveBusinessPartnersByType(cardType: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/bp/inactive/type/${cardType}`);
  }
  
  getInventoryExitByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/inventory/genexit/dates?startDate=${startDate}&endDate=${endDate}`
    );
  }

  getInventoryExitRows(docEntry: string | number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/inventory/genexit/rows/${docEntry}`
    );
  }

  getInventoryExitHeader(docNum: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrlCore}/inventory/genexit/header/${docNum}`
    );
  }

  //--- Geolocalización ---

  getGeofences(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/geofences`
    );
  }

  deleteGeofence(id: number): Observable<any> {
    return this.http.delete<any>(
      `${this.apiUrlCore}/geofence/${id}`
    );
  }

  createGeofence(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrlCore}/geofence`,
      payload
    );
  }

  //--- Systems monitoring ---

  getPendingMobileDocuments(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/mobile/documents/status`
    );
  }

  getErrorMobileDocuments(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/mobile/documents/errors`
    );
  }

  getMobileProcessStatus(): Observable<string> {
    return this.http.get<string>(
      `${this.apiUrlCore}/mobile/process/status`
    );
  }

  cancelDocument(folio: string, type: string): Observable<any> {
    return this.http.patch(`${this.apiUrlCore}/mobile/document/cancel/${folio}/${type}`, {});
  }

  // --- comandos ---

  getCommands(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/commands`
    );
  }

  deleteCommand(id: number | string): Observable<any> {
    return this.http.delete<any>(
      `${this.apiUrlCore}/command/${id}`
    );
  }

  createCommand(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrlCore}/command`,
      payload
    );
  }

  // --- Headers ---
  getInvoiceHeader(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/invoices/header/${folio}`);
  }

  getGenEntryHeader(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/genentry/header/${folio}`);
  }

  getGenExitHeader(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/genexit/header/${folio}`);
  }

  getReturnHeader(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/returns/header/${folio}`);
  }

  getPaymentHeader(folio: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrlCore}/mobile/documents/payments/header/${folio}`);
  }

  getInventoryTransferHeader(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/inventoryTransfers/header/${folio}`);
  }

  getStockTransferHeader(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/stocktransfer/header/${folio}`);
  }

  // --- Rows ---
  getInvoiceRows(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/invoices/rows/${folio}`);
  }

  getGenEntryRows(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/genentry/rows/${folio}`);
  }

  getGenExitRows(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/genexit/rows/${folio}`);
  }

  getReturnRows(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/returns/rows/${folio}`);
  }

  getInventoryTransferRows(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/inventoryTransfers/rows/${folio}`);
  }

  getStockTransferRows(folio: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/stocktransfer/rows/${folio}`);
  }

  // --- Documentos por tipo y rango de fechas ---
  getInvoicesByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/invoices/dates?startDate=${startDate}&endDate=${endDate}`);
  }

  getReturnsByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/returns/dates?startDate=${startDate}&endDate=${endDate}`);
  }

  getGenExitByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/genExit/dates?startDate=${startDate}&endDate=${endDate}`);
  }

  getGenEntryByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/genEntry/dates?startDate=${startDate}&endDate=${endDate}`);
  }

  getPaymentsByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/payments/dates?startDate=${startDate}&endDate=${endDate}`);
  }

  getInventoryTransfersByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/inventoryTransfer/dates?startDate=${startDate}&endDate=${endDate}`);
  }

  getStockTransfersByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile/documents/stockTransfer/dates?startDate=${startDate}&endDate=${endDate}`);
  }

  // --- Employee sale ---

  createSalesInvoice(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrlCore}/sales/invoice`, payload);
  }

  //Series by document

  getSeriesByDocument(document: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/catalogs/seriesnumber/${document}`);
  }

  getSalesTaxCodes(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/catalogs/salesTaxCodes`);
  }

  getProductPrice(itemCode: string, priceList: number, cardCode: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/items/item/price/sell/${itemCode}/${priceList}/${cardCode}`
    );
  }

  getEmployeeSalesByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/sales/invoices/dates?startDate=${startDate}&endDate=${endDate}`
    );
  }

  getEmployeeSaleHeaderByDocNum(docNum: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrlCore}/sales/invoice/header/${docNum}`);
  }

  getEmployeeSaleRows(docEntry: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/sales/invoice/rows/${docEntry}`);
  }
// orden de venta
  getSalesOrdersByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/sales/orders/dates?startDate=${startDate}&endDate=${endDate}`
    );
  }

  createSalesOrder(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrlCore}/sales/orders`, payload);
  }

  getSalesOrderHeaderByDocNum(docNum: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrlCore}/sales/order/header/${docNum}`);
  }

  getSalesOrderRows(docEntry: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/sales/order/rows/${docEntry}`);
  }

  // --- SAP Business One - Ventas ---

  getSalesCreditNotesByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/sales/creditnotes/dates?startDate=${startDate}&endDate=${endDate}`
    );
  }

  getSalesCreditNoteHeader(docNum: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrlCore}/sales/creditnote/header/${docNum}`);
  }

  getSalesCreditNoteRows(docEntry: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/sales/creditnote/rows/${docEntry}`);
  }

  // --- SAP Business One - Socios de negocio ---

  createBusinessPartner(businessPartner: any): Observable<any> {
    return this.http.post(`${this.apiUrlCore}/bp/businesspartner`, businessPartner);
  }

  updateBusinessPartner(payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/bp/businesspartner`, payload);
  }

  getBusinessPartnerAddresses(cardCode: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/bp/addresses/${cardCode}`);
  }

  updateBusinessPartnerAddress(payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/bp/address`, payload);
  }

  updateBusinessPartnerFrequency(payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/bp/businesspartner/frequency`, payload);
  }

  updateBusinessPartnerValidity(payload: { cardCode: string; valid: string }): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/bp/businesspartner/validity`, payload);
  }

  // --- SAP Business One - Solicitudes de Traspaso ---

  getTransferRequestsByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/inventory/transferRequest/dates?startDate=${startDate}&endDate=${endDate}`
    );
  }

  getTransferRequestHeader(docNum: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrlCore}/inventory/transferRequest/header/${docNum}`
    );
  }

  getTransferRequestRows(docEntry: string | number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/inventory/transferRequest/rows/${docEntry}`
    );
  }

  createTransferRequest(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrlCore}/inventory/transferRequests`,
      payload
    );
  }

  // --- SAP Business One - Traspasos ---

  createStockTransfer(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrlCore}/inventory/stockTransfer`,
      payload
    );
  }

  getTransfersByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/inventory/stockTransfer/dates?startDate=${startDate}&endDate=${endDate}`
    );
  }

  getTransferHeader(docNum: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrlCore}/inventory/stockTransfer/header/${docNum}`
    );
  }

  getTransferRows(docEntry: string | number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/inventory/stockTransfer/rows/${docEntry}`
    );
  }



  // --- Almacén mermas ---

  getGCausas(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/userfields/gcausas`);
  }

  getPurchaseItems(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/items/prchse`);
  }

  getAccounts(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/catalogs/accounts`);
  }

  createPurchaseOrder(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrlCore}/purchases/order`, payload);
  }

  getDistributionRules(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/catalogs/distribution/rules/1`);
  }

  getDistributionRulesPlanta(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/catalogs/distribution/rules/2`);
  }

  // --- Campos de usuario dinámicos ---
  getUserFieldCompra(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/userfields/compra`);
  }

  getUserFieldUso(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/userfields/uso`);
  }

  getUserFieldTipo(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/userfields/tipo`);
  }

  getUserFieldPrioridad(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/userfields/prioridad`);
  }

  getUserFieldMotivo(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/userfields/motivo`);
  }

  getUserFieldTipoOV(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/userfields/tipo-OV`);
  }

  getUserFieldPPago(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/userfields/ppago`);
  }

  getUserFieldCobranzaMM(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/userfields/cobranza-mm`);
  }

  // --- Lista de precios de compra prchse ---

  getPurchasePrice(itemCode: string, priceList: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/items/item/price/buy/${itemCode}/${priceList}`);
  }

  getPurchaseOrdersByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/purchases/orders/dates?startDate=${startDate}&endDate=${endDate}`
    );
  }

  getPurchaseOrderHeader(docNum: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrlCore}/purchases/order/header/${docNum}`);
  }

  getPurchaseOrderRows(docEntry: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/purchases/order/rows/${docEntry}`);
  }

  // --- Notas de crédito ---

  createSalesCreditNote(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrlCore}/sales/creditnote`, payload);
  }

  // --- Entrada a inventario ---
  
  getInventoryEntryByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/inventory/genentry/dates?startDate=${startDate}&endDate=${endDate}`
    );
  }

  getReprocessItems(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/items/repro`);
  }

  createInventoryEntry(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrlCore}/inventory/genentry`, payload);
  }

  getInventoryEntryHeader(docNum: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrlCore}/inventory/genentry/header/${docNum}`
    );
  }

  getInventoryEntryRows(docEntry: string | number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/inventory/genentry/rows/${docEntry}`
    );
  }

  getInventoryExitByFolio(folio: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrlCore}/inventory/genexit/folio/${folio}`
    );
  }

  // --- selector items conversion ---

  getConversionItems(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/items/convertion`);
  }

  getConversionPercentage(): Observable<any> {
    return this.http.get<any>(`${this.apiUrlCore}/objects/web/warehouse/prcnt`);
  }

  // --- importador de precios ---

  importItemPrices(prices: any): Observable<any> {
    console.log(prices);
    return this.http.patch(`${this.apiUrlCore}/inventory/price/item`, prices);  
  } 

  getPriceLists(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/priceLists`);
  }

  getBusinessPartnerByCardCode(cardCode: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/bp/${cardCode}`);
  }

  // --- listas de precios ---

  getPriceListsPivot(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/inventory/price-lists/items-price-pivot`
    );
  } 

  // --- Preventas ---

  getPreSaleHerdezByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/mobile/documents/purchase-orders/herdez/dates?startDate=${startDate}&endDate=${endDate}`
    );
  }

  // --- Visitas ---

  getVisitsByDateRange(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/objects/app/visits?startDate=${startDate}&endDate=${endDate}`
    );
  }

  // ---promociones ---

  createOneToOnePromotion(promotion: any): Observable<any> {
    return this.http.post(`${this.apiUrlCore}/promotions/promotion/one`, promotion);
  }

  getOneToOnePromotions(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/promotions/one`);
  }

  assignOneToOnePromotionCustomer(data: any): Observable<any> {
    return this.http.post(`${this.apiUrlCore}/promotions/promotion/one/customer`, data);
  }

  getOneToOnePromotionCustomers(promotionId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/promotions/one/customers/${promotionId}`);
  }

  deleteAllPromotionCustomers(promotionId: number): Observable<any> {
    return this.http.delete(`${this.apiUrlCore}/promotions/promotion/one/customer/all/${promotionId}`);
  }

  deleteOneToOnePromotion(promotionId: number): Observable<any> {
    return this.http.delete(`${this.apiUrlCore}/promotions/promotion/one/${promotionId}`);
  }

  deletePromotionCustomer(promotionId: number, cardCode: string): Observable<any> {
    return this.http.delete(`${this.apiUrlCore}/promotions/promotion/one/customer/${promotionId}/${cardCode}`);
  }

  updateOneToOnePromotion(promotion: any): Observable<any> {
    return this.http.patch(`${this.apiUrlCore}/promotions/promotion/one`, promotion);
  }

  createVolumePromotion(promotion: any): Observable<any> {
    return this.http.post(`${this.apiUrlCore}/promotions/promotion/volumen`, promotion);
  }

  getVolumePromotions(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/promotions/volumen`);
  }

  // Obtener listas de precios asignadas a una promoción por volumen
  getVolumePromotionPriceLists(promotionId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/promotions/volumen/priceslists/${promotionId}`);
  }

  // Asignar lista de precios a promoción por volumen
  assignVolumePromotionPriceList(data: any): Observable<any> {
    return this.http.post(`${this.apiUrlCore}/promotions/promotion/volumen/pricelist`, data);
  }

  // Eliminar lista de precios de promoción por volumen
  deleteVolumePromotionPriceList(promotionId: number, listNum: number): Observable<any> {
    return this.http.delete(`${this.apiUrlCore}/promotions/promotion/volumen/pricelist/${promotionId}/${listNum}`);
  }

  deleteAllVolumePromotionPriceLists(promotionId: number): Observable<any> {
    return this.http.delete(`${this.apiUrlCore}/promotions/promotion/volumen/pricelist/all/${promotionId}`);
  }

  deleteVolumePromotion(promotionId: number): Observable<any> {
    return this.http.delete(`${this.apiUrlCore}/promotions/promotion/volumen/${promotionId}`);
  }

  updateVolumePromotion(promotion: any): Observable<any> {
    return this.http.patch(`${this.apiUrlCore}/promotions/promotion/volumen`, promotion);
  }

  // Activar y desactivar promos

  updateOneToOneValid(payload: { id: number, validFor: string }): Observable<any> {
    return this.http.patch(`${this.apiUrlCore}/promotions/promotion/one/validfor`, payload);
  }

  updateVolumeValid(payload: { id: number, validFor: string }): Observable<any> {
    return this.http.patch(`${this.apiUrlCore}/promotions/promotion/volumen/valid`, payload);
  }

  // --- Encuestas ---

  createGoogleSurvey(survey: any): Observable<any> {
    return this.http.post(`${this.apiUrlCore}/surveys/google`, survey);
  }

  getGoogleSurveys(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/surveys/google`);
  }

  deleteGoogleSurvey(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrlCore}/surveys/google/${id}`);
  }

  // --- Endpoints socios de negocio ---

  getCardGroups(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/cardGroups`);
  }

  getPaymentTerms(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/paymentTerms`);
  }

  /*getPriceLists(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/priceLists`);
  }*/

  getSalesEmployees(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/salesEmployee`);
  }

  getCurrencyCodes(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/currencyCodes`);
  }

  getIndustries(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/industries`);
  }

  getCardMPago(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/udo/cardMPago`);
  }

  getCardTipoCte(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/udo/cardTipoCte`);
  }

  getCardClasCte(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/udo/cardClasCte`);
  }

  getCardFacturacionCO(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/udo/cardFacturacionCO`);
  }

  getCountries(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/countries`);
  }

  getStates(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/states`);
  }

  getCardPromoCte(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/userfields/card/promo-cte`);
  }

  // --- Token de instalación ---

  generateInstallToken(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/app/auth/install`);
  }

  saveInstallToken(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/app/auth/install`, payload);
  }

  // --- Promotoras ---
  getCatalogEmployees(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/catalogs/employees`);
  }

  createItinerary(payload: any): Observable<any> {
    return this.http.post(`${this.apiUrlCore}/itinerary`, payload);
  }

  // Buscar visitas por usuario y rango de fechas
  getItineraryByUserAndDates(uLMUser: string, startDate: string, endDate: string): Observable<any[]> {
    const params = new HttpParams()
      .set('uLMUser', uLMUser)
      .set('StartDate', startDate)
      .set('EndDate', endDate);
    return this.http.get<any[]>(`${this.apiUrlCore}/itinerary/user/dates`, { params });
  }

  // Eliminar visita por ID
  deleteItinerary(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrlCore}/itinerary/id/${id}`);
  }

  deleteItineraryByUser(uLMUser: string): Observable<any> {
    return this.http.delete(`${this.apiUrlCore}/itinerary/user/${uLMUser}`);
  }

  // Reportes itinerarios
  getPromoterInitialDates(start: string, end: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/promoters/promoter-initial/dates?StartDate=${start}&EndDate=${end}`
    );
  }

  getPromoterPresentationDates(start: string, end: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/promoters/promoter-presentation/dates?StartDate=${start}&EndDate=${end}`
    );
  }

  getPromoterCoolRoomDates(start: string, end: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/promoters/promoter-cool-room/dates?StartDate=${start}&EndDate=${end}`
    );
  }

  getPromoterSalesFloorDates(start: string, end: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/promoters/promoter-sales-floor/dates?StartDate=${start}&EndDate=${end}`
    );
  }

  getPromoterSalesFloorPhotosDates(start: string, end: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/promoters/promoter-sales-floor-photos/dates?StartDate=${start}&EndDate=${end}`
    );
  }

  getPromoterSolicitudeDates(start: string, end: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/promoters/promoter-solicitude/dates?StartDate=${start}&EndDate=${end}`
    );
  }

  getPromoterClosureDates(start: string, end: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/promoters/promoter-closure/dates?StartDate=${start}&EndDate=${end}`
    );
  }

  // Pedidos sugeridos
  getSuggestedOrdersBySP(slpCode: number, docDate: string, riskPercentage: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/suggested-order/sp`, {
      params: {
        slpCode: slpCode.toString(),
        docDate: docDate,
        riskPercentage: riskPercentage.toString()
      }
    });
  }

  getEmployeesInfo(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/employeesInfo`);
  }

  updateEmployeeOrderMode(employeeID: number, uModoPedido: string): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/employeesInfo`, { 
      employeeID: employeeID, 
      uModoPedido: uModoPedido 
    });
  }

  // Risk percentage
  getRiskPercentage(): Observable<any> {
    return this.http.get<any>(`${this.apiUrlCore}/risk-percentage`);
  }

  createRiskPercentage(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrlCore}/risk-percentage`, payload);
  }

  updateRiskPercentage(payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/risk-percentage`, payload);
  }
  

  // --- Logística ---

  getVehicles(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/objects/app/vehicles`);
  }

  getVehicleByUnitNumber(unitNumber: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrlCore}/objects/app/vehicle/${unitNumber}`);
  }

  createVehicle(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrlCore}/objects/app/vehicles`, payload);
  }

  updateVehicle(payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/objects/app/vehicles/`, payload);
  }

  deleteVehicle(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrlCore}/objects/app/vehicle/${id}`);
  }

  getWeeklyInspections(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/objects/app/vehicle/inspection/weekly?startDate=${startDate}&endDate=${endDate}`
    );
  }

  getWeeklyInspectionByFolio(folio: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrlCore}/objects/app/vehicle/inspection/weekly/${folio}`
    );
  }

  getDailyInspections(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/objects/app/vehicle/inspection/daily?startDate=${startDate}&endDate=${endDate}`
    );
  }

  getDailyInspectionByFolio(folio: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrlCore}/objects/app/vehicle/inspection/daily/${folio}`
    );
  }

  // --- Versions ---

  getVersions(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/objweb/versions`);
  }

  updateVersion(payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/objweb/version/update`, payload);
  }

  deleteVersion(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/objweb/version/delete/${id}`);
  }

  // --- Pagos masivos ---

  importIncomingPayment(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrlCore}/payments/incoming`,
      payload
    );
  }

  // --- Facturas sin promesa de pago ---

  updateSalesInvoice(payload: any): Observable<any> {
    return this.http.patch(`${this.apiUrlCore}/sales/invoice`, payload);
  }

  //--- asignación de equipos ---

  getMobileAssignments(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile-assignments`);
  }

  deleteMobileAssignment(uLMUser: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrlCore}/mobile-assignment/user/${uLMUser}`);
  }

  createMobileAssignment(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrlCore}/mobile-assignment`, payload);
  }

  updateMobileAssignment(payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/mobile-assignment`, payload);
  }

  getMobileAssignmentByUser(uLMUser: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/mobile-assignment/user/${uLMUser}`);
  }

  //--- bloquear usuarios ---

  getCatalogUsers(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/catalogs/users`);
  }

  lockUser(userId: number): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/admon/users/locked/${userId}`, {});
  }

  unlockUser(userId: number): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/admon/users/unlocked/${userId}`, {});
  }

  //--- precios especiales ---

  createSpecialPrice(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrlCore}/inventory/specialprice`, payload);
  }

  getClientSpecialPrices(cardCode: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/inventory/specialprices/${cardCode}`);
  }

  updateSpecialPrice(payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/inventory/specialprice`, payload);
  }

  //--- campo minado ---

  getProductsWithProperties(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/items/properties/sell`);
  }

  updateItemProperties(payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/inventory/item/properties`, payload);
  }

  //--- cobranza multi tickets ---

  getMultiTicketCollection(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/admon/collection/multi-tickets?startDate=${startDate}&endDate=${endDate}`
    );
  }

  //--- Administración ---
  //--- porcentajes almacén ---

  getWarehousePercentages(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrlCore}/objects/web/warehouse/prcnt`);
  }

  updateWarehousePercentage(payload: { id: number; name: string; percentage: number }): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlCore}/objects/web/warehouse/prcnt`, payload);
  }

  //--- estatus de timbrado ---

  getElectronicProtocolsByDate(startDate: string, endDate: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrlCore}/sales/invoices/electroni-protocols/dates?startDate=${startDate}&endDate=${endDate}`
    );
  }

  stampElectronicProtocol(docEntry: number): Observable<any> {
    return this.http.patch(`${this.apiUrlCore}/sales/invoice/electronic-protocol/${docEntry}`, {});
  }
}